import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { makeClassName } from '@siteless/util';
import type { MobileScoredListHandle } from './MobileScoredList';
import ScoreAlert from './ScoreAlert';
import ViewBallotModal from './ViewBallotModal';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import { CurrentUserResourceType } from '../resources/Auth/CurrentUser';
import ObjectiveStateResource from '../resources/ObjectiveState';
import BallotsScoresResource from '../resources/Ballot/Scores';
import { OneBallotResourceType } from '../resources/Ballot/One';
import { OneLeagueResourceType } from '../resources/League/One';
import {
  ScrolledOffUser,
  SCROLLED_OFF_BOTTOM,
  SCROLLED_OFF_TOP,
} from './ScrolledOffUser';
import { ScrollToTopButton } from './ScrollToTopButton';
import avatars from '../config/avatars';
import { getUsersByScoreList } from '../util/getUsersByScoreList';
import { LeagueIncompleteBallotTier } from './LeagueIncompleteBallotTier';
import LeagueBar, { LeagueBarCountMode } from './LeagueBar';
import { usePresentationMode } from '../util/usePresentationMode';
import { PersistentBanner } from './PersistentBanner';
import { CeremonyDetailCard } from './CeremonyDetailCard';
import { ClusterBallotTier } from './ClusterBallotTier';
import BallotsCompletedResource from '../resources/Ballot/Completed';
import SpoilerDelayModal from './SpoilerDelayModal';
import { updateUserPropertiesMutation } from '../resources/Auth/mutations';
import DesktopLeagueControls from './DesktopLeagueControls';
import PreScoringUserList from './PreScoringUserList';
import LeagueSettingsModal from './LeagueSettingsModal';
import MobileScoredList, {
  type FlatItem,
  FlatItemType,
} from './MobileScoredList';
import { FireworksOverlay } from './FireworksOverlay';
import { useMeasure } from '../util/useMeasure';
import { getIsFinalApproach } from '../util/getIsFinalApproach';
import SpectatorModal from './SpectatorModal';
import { BrowserStorageResource } from '../resources/BrowserStorage';
import { writeBrowserStorage } from '../resources/writeBrowserStorage';
import { thisOscarsYear } from '../config/constants';

let CeremonyDuring = ({
  ballot,
  leagues,
  currentUser,
}: {
  ballot: OneBallotResourceType;
  leagues: NonNullable<OneLeagueResourceType>[];
  currentUser: CurrentUserResourceType;
}) => {
  let objectiveState = ObjectiveStateResource.use();
  let ballotScores = BallotsScoresResource.use();
  let completedBallots = BallotsCompletedResource.use();

  let allEnums = AllUserProfilesResource.use();

  // filters

  let [isPending, startTransition] = useTransition();
  let [focusedLeagueId, _setFocusedLeagueId] = useState<string | undefined>();
  let setFocusedLeagueId = useCallback((id: string | undefined) => {
    startTransition(() => {
      _setFocusedLeagueId(id);
    });
  }, []);
  let focusedLeague = useMemo(
    () => leagues.find((league) => league.id === focusedLeagueId) || null,
    [focusedLeagueId, leagues],
  );
  let filterToUserIds = React.useMemo(
    () => focusedLeague?.memberUserIds || null,
    [focusedLeague],
  );

  // scoring

  let useStrictScore = focusedLeague?.strictMode || false;

  let hasScoringStarted = useMemo(
    () => Boolean(Object.values(ballotScores).find((item) => item.score > 0)),
    [ballotScores],
  );

  let usersByScore = useMemo(
    () =>
      getUsersByScoreList({
        completedBallots,
        ballotScores,
        allEnums,
        useStrictScore,
        currentUserId: currentUser.id,
        filterToUserIds,
      }),
    [
      ballotScores,
      completedBallots,
      allEnums,
      filterToUserIds,
      useStrictScore,
      currentUser.id,
    ],
  );

  let scoreTiers = useMemo(() => {
    let result: Record<
      number,
      {
        points: number;
        users: typeof usersByScore;
      }
    > = {};
    usersByScore.forEach((user) => {
      let scoreKey = user.score || 0;

      if (!result[scoreKey])
        result[scoreKey] = {
          points: scoreKey,
          users: [],
        };

      result[scoreKey].users.push(user);
    });

    return Object.values(result).sort((a, b) => b.points - a.points);
  }, [usersByScore]);

  let incompleteUsers = useMemo(() => {
    if (!focusedLeague) return [];
    return (focusedLeague.memberUserIds || [])
      .filter((userId) => !completedBallots[userId])
      .map((userId) => {
        let user = allEnums.users[userId];
        if (!user) return null;
        return {
          key: userId,
          ArtworkComponent:
            avatars[user.avatarId || 'AVATAR_0'].ArtworkComponent,
          handle: user.handle,
          avatarBackgroundHex: user.avatarBackgroundHex,
        };
      })
      .filter(Boolean) as {
      key: string;
      ArtworkComponent: any;
      handle?: string;
      avatarBackgroundHex?: string | null;
    }[];
  }, [focusedLeague, completedBallots, allEnums]);

  let { flatList, currentUserListIndex, lastWinnerIndex } = useMemo(() => {
    let flatList: FlatItem[] = [];
    let currentUserListIndex = -1;
    let runningUserIndex = 0;

    for (let i = 0; i < scoreTiers.length; i++) {
      let tier = scoreTiers[i];
      flatList.push({
        type: FlatItemType.header,
        rank: i + 1,
        points: tier.points,
        tierSize: tier.users.length,
      });

      for (let user of tier.users) {
        if (user.key === currentUser.id) currentUserListIndex = flatList.length;
        flatList.push({
          type: FlatItemType.user,
          user,
          withinGroupIndex: runningUserIndex,
          points: tier.points,
        });
        runningUserIndex++;
      }
    }

    if (incompleteUsers.length > 0) {
      flatList.push({
        type: FlatItemType.header,
        rank: scoreTiers.length + 1,
        points: 0,
        tierSize: incompleteUsers.length,
        incompleteTier: true,
      });
      flatList.push({
        type: FlatItemType.incompleteCluster,
        users: incompleteUsers,
      });
    }

    flatList.push({ type: FlatItemType.titanic });

    let lastWinnerIndex = scoreTiers[0] ? scoreTiers[0].users.length : -1;
    return { flatList, currentUserListIndex, lastWinnerIndex };
  }, [scoreTiers, currentUser.id, incompleteUsers]);

  let currentUserBallotScore = useStrictScore
    ? ballotScores[currentUser.id]?.strictScore || 0
    : ballotScores[currentUser.id]?.score || 0;

  let currentUserRankIndex = React.useMemo(
    () =>
      scoreTiers.findIndex(({ points }) => points === currentUserBallotScore),
    [scoreTiers, currentUserBallotScore],
  );

  let [showScoreChange, setShowScoreChange] = useState<undefined | number>();
  let [lastScore, setLastScore] = useState<undefined | number>();

  useEffect(() => {
    let change =
      lastScore !== undefined ? currentUserBallotScore - lastScore : 0;
    if (change > 0) setShowScoreChange(change);
    setLastScore(currentUserBallotScore);
  }, [currentUserBallotScore, lastScore]);

  let handleClearScoreChange = useCallback(() => {
    setShowScoreChange(undefined);
  }, []);

  // spectator mode

  let browserStorage = BrowserStorageResource.use();
  let isSpectator = !completedBallots[currentUser.id];
  let showSpectatorModal =
    isSpectator &&
    !browserStorage?.dismissedSpectatorNotificationForYears?.[
      String(thisOscarsYear)
    ];

  // ballot inspect

  let [focusedBallotKey, setFocusedBallotKey] = useState<undefined | string>();
  let [showSpoilerDelayModal, setShowSpoilerDelayModal] = useState(false);
  let [settingsModalLeagueId, setSettingsModalLeagueId] = useState<
    string | undefined
  >();
  let focusedUserBallotScore = useStrictScore
    ? (focusedBallotKey && ballotScores[focusedBallotKey]?.strictScore) || 0
    : (focusedBallotKey && ballotScores[focusedBallotKey]?.score) || 0;
  let focusedUserStrictScore =
    (focusedBallotKey && ballotScores[focusedBallotKey]?.strictScore) || 0;

  let focusedUserRankIndex = useMemo(
    () =>
      scoreTiers.findIndex(({ points }) => points === focusedUserBallotScore),
    [scoreTiers, focusedUserBallotScore],
  );

  let scrollContainerRef = React.useRef<null | HTMLDivElement>(null);
  let [mobileContainerRef, mobileContainerRect] = useMeasure();
  let mobileListRef = useRef<MobileScoredListHandle>(null);
  let currentUserItemRef = React.useRef<null | HTMLDivElement>(null);
  let getOffScreenDirection = React.useCallback(() => {
    let targetElement = currentUserItemRef.current;
    let containerElement = scrollContainerRef.current;
    if (!(targetElement && containerElement)) return;

    let targetElementDistanceToTop = targetElement.offsetTop;
    let targetElementHeight = targetElement.offsetHeight;
    let triggerPadding = 25;

    let containerElementScrollTop = containerElement.scrollTop;
    let containerElementHeight = containerElement.offsetHeight;

    let isScrolledOffTop =
      targetElementHeight +
        targetElementDistanceToTop -
        containerElementScrollTop -
        triggerPadding <
      0;

    let isScrolledOffBottom =
      containerElementScrollTop +
        containerElementHeight -
        targetElementDistanceToTop -
        triggerPadding <
      0;

    return isScrolledOffBottom
      ? SCROLLED_OFF_BOTTOM
      : isScrolledOffTop
      ? SCROLLED_OFF_TOP
      : undefined;
  }, []);

  let [visibleRange, setVisibleRange] = useState({
    startIndex: 0,
    endIndex: 0,
    inLastPage: false,
  });

  let winnersVisible =
    lastWinnerIndex >= 0 && visibleRange.startIndex <= lastWinnerIndex;

  let fireworkTargetBounds = useCallback(
    (canvas: { width: number; height: number }) => {
      let dpr = canvas.height / window.innerHeight;
      return {
        top: mobileContainerRect.top * dpr,
        bottom: mobileContainerRect.bottom * dpr,
      };
    },
    [mobileContainerRect.top, mobileContainerRect.bottom],
  );
  let isFinalApproach = getIsFinalApproach({
    rankCount: scoreTiers.length,
    inLastRank:
      visibleRange.endIndex >=
      flatList.length -
        1 -
        (scoreTiers[scoreTiers.length - 1]?.users.length ?? 0),
    inLastPage: visibleRange.inLastPage,
    winnersVisible,
  });

  let mobileOffscreenDirection = useMemo(() => {
    if (currentUserListIndex < 0) return undefined;
    if (currentUserListIndex < visibleRange.startIndex) return SCROLLED_OFF_TOP;
    if (currentUserListIndex > visibleRange.endIndex)
      return SCROLLED_OFF_BOTTOM;
    return undefined;
  }, [currentUserListIndex, visibleRange]);

  let [currentUserOffscreenDirection, setCurrentUserOffscreenDirection] =
    useState<typeof SCROLLED_OFF_BOTTOM | typeof SCROLLED_OFF_TOP | undefined>(
      undefined,
    );

  let handleScrollToTop = React.useCallback(() => {
    if (mobileListRef.current) {
      mobileListRef.current.scrollToOffset(0, {
        behavior: 'smooth',
      });
    }
  }, []);

  let handleScrollToCurrentUser = React.useCallback(() => {
    if (mobileListRef.current && currentUserListIndex >= 0) {
      mobileListRef.current.scrollToIndex(currentUserListIndex, {
        align: 'center',
        behavior: 'smooth',
      });
    } else if (currentUserItemRef.current) {
      currentUserItemRef.current.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [currentUserListIndex]);

  useEffect(() => {
    if (usersByScore) setCurrentUserOffscreenDirection(getOffScreenDirection());
  }, [usersByScore, getOffScreenDirection]);

  let [presentationMode] = usePresentationMode();

  return (
    <>
      {focusedBallotKey && (
        <ViewBallotModal
          isOpen
          onClose={() => {
            setFocusedBallotKey(undefined);
          }}
          ballotKey={focusedBallotKey}
          score={hasScoringStarted ? focusedUserBallotScore : undefined}
          strictScore={hasScoringStarted ? focusedUserStrictScore : undefined}
          rank={hasScoringStarted ? focusedUserRankIndex + 1 : undefined}
          scoreIsStrict={useStrictScore}
          isOwner={focusedLeague?.ownerUserId === focusedBallotKey}
        />
      )}
      <SpoilerDelayModal
        isOpen={showSpoilerDelayModal}
        onClose={() => setShowSpoilerDelayModal(false)}
      />
      <SpectatorModal
        isOpen={showSpectatorModal}
        onClose={() => {
          writeBrowserStorage((last) => ({
            ...last,
            dismissedSpectatorNotificationForYears: {
              ...(last?.dismissedSpectatorNotificationForYears || {}),
              [String(thisOscarsYear)]: true,
            },
          }));
        }}
      />
      {settingsModalLeagueId && (
        <LeagueSettingsModal
          isOpen
          onClose={() => setSettingsModalLeagueId(undefined)}
          leagueId={settingsModalLeagueId}
          showShareButton
        />
      )}
      {presentationMode ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="relative z-10 -mb-[60px]">
            <DesktopLeagueControls
              leagues={leagues}
              completedBallots={completedBallots}
              focusedLeagueId={focusedLeagueId}
              onSelectLeague={setFocusedLeagueId}
              onOpenDelayModal={() => setShowSpoilerDelayModal(true)}
              onDismissDelay={() => {
                updateUserPropertiesMutation({
                  userId: currentUser.id,
                  spoilerDelayMs: 0,
                });
              }}
              onOpenSettingsModal={setSettingsModalLeagueId}
              delayMs={currentUser.spoilerDelayMs}
            />
          </div>
          <div className="flex min-h-0 flex-1 pb-4 pl-4">
            <div className="flex w-[360px] flex-none flex-col pt-[60px]">
              <CeremonyDetailCard className="flex-1 px-4 pb-4" />
            </div>
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="relative z-0 min-h-0 flex-1 overflow-hidden">
                {hasScoringStarted ? (
                  <div
                    className={makeClassName(
                      'relative z-0 h-full w-full overflow-auto pb-8 pt-16 transition-opacity',
                      isPending && 'opacity-50',
                    )}
                    onScroll={() => {
                      setCurrentUserOffscreenDirection(getOffScreenDirection());
                    }}
                    ref={scrollContainerRef}
                  >
                    {/* TODO: add scroll detection to toggle active when winners scroll off */}
                    {scoreTiers[0]?.points > 0 && (
                      <FireworksOverlay
                        colors={scoreTiers[0].users.map(
                          (u) => u.avatarBackgroundHex,
                        )}
                        active={objectiveState.didCeremonyEnd}
                      />
                    )}
                    {scoreTiers.map((tier, index) => (
                      <ClusterBallotTier
                        key={tier.points}
                        points={tier.points}
                        rank={index + 1}
                        hasScoringStarted={hasScoringStarted}
                        useStrictScore={useStrictScore}
                        ownerUserId={focusedLeague?.ownerUserId}
                        onSelectBallot={setFocusedBallotKey}
                        userBallots={tier.users}
                      />
                    ))}
                    {focusedLeagueId && (
                      <LeagueIncompleteBallotTier leagueId={focusedLeagueId} />
                    )}
                  </div>
                ) : (
                  <PreScoringUserList
                    className={makeClassName(
                      'relative z-0 h-full w-full transition-opacity',
                      isPending && 'opacity-50',
                    )}
                    filterToUserIds={filterToUserIds}
                    ownerUserId={focusedLeague?.ownerUserId}
                    onSelectBallot={setFocusedBallotKey}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ScoreAlert
            score={showScoreChange}
            onClear={handleClearScoreChange}
          />
          <PersistentBanner
            ballot={ballot}
            onOpenDelayModal={() => setShowSpoilerDelayModal(true)}
            delayMs={currentUser.spoilerDelayMs}
            isSpectator={isSpectator}
            winner={
              scoreTiers[0]
                ? {
                    handles: scoreTiers[0].users
                      .map((u) => u.handle)
                      .filter((h): h is string => Boolean(h)),
                    points: scoreTiers[0].points,
                  }
                : undefined
            }
            onOpenSpectatorInfo={() => {
              writeBrowserStorage((last) => ({
                ...last,
                dismissedSpectatorNotificationForYears: {
                  ...(last?.dismissedSpectatorNotificationForYears || {}),
                  [String(thisOscarsYear)]: false,
                },
              }));
            }}
          />
          <LeagueBar
            leagues={leagues}
            completedBallots={completedBallots}
            focusedLeagueId={focusedLeagueId}
            onSelectLeague={setFocusedLeagueId}
            countMode={LeagueBarCountMode.completedBallots}
            selectable
            currentUserId={currentUser.id}
            onOpenSettings={setSettingsModalLeagueId}
            className="relative z-10 -mb-[60px] flex h-[60px] items-center overflow-auto px-3"
          />
          <div
            ref={mobileContainerRef}
            className="relative z-0 min-h-0 flex-1 overflow-hidden"
          >
            <ScrolledOffUser
              // needed to re-trigger positioning
              key={String(currentUser.prefersLeftHandMode)}
              currentUser={currentUser}
              shouldShow={Boolean(
                hasScoringStarted
                  ? mobileOffscreenDirection
                  : currentUserOffscreenDirection,
              )}
              offScreenDirection={
                hasScoringStarted
                  ? mobileOffscreenDirection
                  : currentUserOffscreenDirection
              }
              onRequestScrollTo={handleScrollToCurrentUser}
              currentUserRankIndex={currentUserRankIndex}
            />
            <ScrollToTopButton
              shouldShow={hasScoringStarted && !winnersVisible}
              onScrollToTop={handleScrollToTop}
              prefersLeftHandMode={currentUser.prefersLeftHandMode}
            />
            {hasScoringStarted ? (
              <>
                {scoreTiers[0]?.points > 0 && (
                  <FireworksOverlay
                    colors={scoreTiers[0].users.map(
                      (u) => u.avatarBackgroundHex,
                    )}
                    active={winnersVisible && objectiveState.didCeremonyEnd}
                    targetBounds={fireworkTargetBounds}
                  />
                )}
                <MobileScoredList
                  flatList={flatList}
                  currentUserId={currentUser.id}
                  ownerUserId={focusedLeague?.ownerUserId}
                  hasScoringStarted={hasScoringStarted}
                  useStrictScore={useStrictScore}
                  isPending={isPending}
                  isFinalApproach={isFinalApproach}
                  prefersLeftHandMode={currentUser.prefersLeftHandMode}
                  onSelectBallot={setFocusedBallotKey}
                  onRangeChange={setVisibleRange}
                  ref={mobileListRef}
                />
              </>
            ) : (
              <PreScoringUserList
                className={makeClassName(
                  'relative z-0 h-full w-full transition-opacity',
                  isPending && 'opacity-50',
                )}
                filterToUserIds={filterToUserIds}
                onSelectBallot={setFocusedBallotKey}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default CeremonyDuring;
